<template>
  <div :class="`col-sm-6 col-lg-${col}`">
    <div :class="`card metric-card card-${color}`">
      <div class="card-body">
        <!-- Skeleton -->
        <template v-if="!status">
          <div class="d-flex justify-content-between align-items-start">
            <div class="w-75">
              <div class="skeleton sk-label"></div>
              <div class="skeleton sk-value mt-2"></div>
            </div>
            <div class="skeleton sk-icon"></div>
          </div>
          <div class="d-flex justify-content-end mt-3">
            <div class="skeleton sk-today"></div>
          </div>
        </template>

        <!-- Data -->
        <template v-else>
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <div class="m-label">{{ title }}</div>
              <div class="m-value">{{ fmt(totalGen) }}</div>
            </div>
            <div class="m-icon">
              <i :class="icon"></i>
            </div>
          </div>

          <div class="d-flex justify-content-end" style="height:22px">
            <div v-if="today" class="m-today">
              <i class="fas fa-calendar-day me-1"></i>
              today : {{ fmt(totalCurrent) }}
            </div>
          </div>
        </template>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
withDefaults(defineProps<{
  title: string
  totalGen: string
  totalCurrent: string
  icon: string
  color: 
    | 'process'
    | 'pending'
    | 'failed'
    | 'error'
    | 'success'
    | 'warning'
    | 'info'
  status: boolean
  today?: boolean
  col?: number
}>(), { today: true, col: 2 })

const fmt = (v: string) => Number(v).toLocaleString('en-US')
</script>

<style scoped>
  .metric-card {
    border: none;
    border-radius: 14px;
    background: #fff;
    margin-bottom: 1.2rem;
    box-shadow: 0 4px 16px rgba(0,0,0,.06);
    transition: transform .3s, box-shadow .3s;
  }

  .metric-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 24px rgba(0,0,0,.1);
  }

  .card-process,
  .card-pending,
  .card-primary,
  .card-secondary,
  .card-failed,
  .card-error,
  .card-success,
  .card-warning,
  .card-info {
    --card-color: #999;
    border-top: 3px solid var(--card-color);
  }

  .card-process { --card-color: #0cad4a; }
  .card-pending { --card-color: #4e73df; }
  .card-primary { --card-color: #1a4de4; }
  .card-secondary { --card-color: #9599a5; }
  .card-failed  { --card-color: #f6c23e; }
  .card-error   { --card-color: #e74a3b; }
  .card-success { --card-color: #1cc88a; }
  .card-warning { --card-color: #f39c12; }
  .card-info    { --card-color: #36b9cc; }

  .m-icon, .sk-icon {
    width: 52px;
    height: 52px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    flex-shrink: 0;
  }

  .m-icon {
    background: color-mix(in srgb, var(--card-color) 15%, white);
    color: var(--card-color);
  }

  .m-label {
    font-size: .8rem;
    font-weight: 600;
    letter-spacing: .4px;
    color: #858796;
  }

  .m-value {
    font-size: 2rem;
    font-weight: 800;
    color: #2e3a59;
    line-height: 1;
    margin: .35rem 0;
  }

  .m-today {
    font-size: .82rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    color: var(--card-color);
  }

  .skeleton {
    background: linear-gradient(90deg, #ececec 25%, #f4f4f4 50%, #ececec 75%);
    background-size: 200% 100%;
    animation: shimmer 1.4s infinite;
    border-radius: 6px;
  }

  @keyframes shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  .sk-label { width: 60%; height: 13px; }
  .sk-value { width: 45%; height: 30px; border-radius: 8px; }
  .sk-today { width: 38%; height: 14px; }
  .sk-icon  { border-radius: 10px; }

  @media (max-width: 768px) {
    .m-value { font-size: 1.6rem; }
  }
</style>